import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { Property } from '../../types/property';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface VisitVoucherDataPDF {
  firstName?: string;
  lastName?: string;
  phone?: string;
  idType?: 'passport' | 'id-card';
  idNumber?: string;
}

interface VisitVoucherPDFProps {
  property: Property;
  agencyName: string;
  agencyAddress: string;
  agencyPhone: string;
  brandColor?: string;
  visitData?: VisitVoucherDataPDF;
}

export const VisitVoucherPDF: React.FC<VisitVoucherPDFProps> = ({
  property,
  agencyName,
  agencyAddress,
  agencyPhone,
  brandColor = '#0F766E',
  visitData,
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      padding: 25,
      height: '100%',
    },
    header: {
      marginBottom: 15,
      alignItems: 'center',
    },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 5,
      textAlign: 'center',
    },
    subtitle: {
      fontSize: 11,
      color: '#6B7280',
      textAlign: 'center',
      marginBottom: 15,
    },
    section: {
      marginBottom: 12,
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 8,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
      paddingLeft: 8,
    },
    infoGrid: {
      marginBottom: 8,
    },
    infoRow: {
      flexDirection: 'row',
      marginBottom: 4,
      width: '100%',
    },
    infoLabel: {
      width: '40%',
      fontSize: 10,
      color: '#6B7280',
      fontWeight: 'bold',
    },
    infoValue: {
      width: '60%',
      fontSize: 10,
      color: '#111827',
    },
    visitSection: {
      backgroundColor: '#F9FAFB',
      padding: 10,
      borderRadius: 6,
      marginBottom: 12,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
    },
    visitTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 8,
    },
    visitGrid: {
      flexDirection: 'row',
      gap: 20,
    },
    visitColumn: {
      flex: 1,
    },
    visitField: {
      marginBottom: 6,
    },
    visitLabel: {
      fontSize: 9,
      color: '#6B7280',
      marginBottom: 2,
    },
    visitInput: {
      borderBottomWidth: 1,
      borderBottomColor: '#D1D5DB',
      paddingBottom: 2,
      minHeight: 12,
    },
    visitValue: {
      fontSize: 10,
      color: '#111827',
      paddingBottom: 2,
      minHeight: 12,
    },
    legalSection: {
      backgroundColor: '#FEF3C7',
      padding: 8,
      borderRadius: 4,
      marginBottom: 12,
    },
    legalTitle: {
      fontSize: 10,
      fontWeight: 'bold',
      color: '#92400E',
      marginBottom: 4,
    },
    legalText: {
      fontSize: 8,
      color: '#92400E',
      lineHeight: 1.3,
      textAlign: 'justify',
    },
    exclusivitySection: {
      backgroundColor: '#FEE2E2',
      padding: 8,
      borderRadius: 4,
      marginBottom: 12,
    },
    exclusivityTitle: {
      fontSize: 10,
      fontWeight: 'bold',
      color: '#DC2626',
      marginBottom: 4,
    },
    exclusivityText: {
      fontSize: 9,
      fontWeight: 'bold',
      color: '#DC2626',
      lineHeight: 1.4,
      textAlign: 'justify',
    },
    signatures: {
      marginTop: 15,
      flexDirection: 'row',
      justifyContent: 'space-around',
    },
    signatureBox: {
      width: '40%',
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      paddingTop: 15,
      alignItems: 'center',
    },
    signatureTitle: {
      fontSize: 9,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 3,
    },
    signatureSubtitle: {
      fontSize: 8,
      color: '#6B7280',
      textAlign: 'center',
      marginBottom: 25,
    },
    footer: {
      marginTop: 15,
      textAlign: 'center',
      fontSize: 8,
      color: '#9CA3AF',
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      paddingTop: 8,
    },
  });

  const formatPrice = (price: number) => {
    return Math.floor(price || 0).toLocaleString('fr-FR').replace(/\s/g, ' ') + ' DA';
  };

  const getPropertyType = () => {
    if (property.property_category) {
      return property.property_subtype && property.property_subtype !== 'Simple'
        ? `${property.property_category} ${property.property_subtype}`
        : property.property_category;
    }
    return property.type || 'Bien immobilier';
  };

  const getIdTypeLabel = (idType?: string) => {
    if (idType === 'passport') return 'Passeport';
    if (idType === 'id-card') return 'Carte d\'identité';
    return '';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête */}
        <View style={styles.header}>
          <Text style={styles.title}>BON DE VISITE</Text>
          <Text style={styles.subtitle}>
            Conforme à la réglementation algérienne - Article 15 de la loi n°08-15
          </Text>
        </View>

        {/* Informations de l'agence */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Agence immobilière</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Nom :</Text>
              <Text style={styles.infoValue}>{agencyName}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Adresse :</Text>
              <Text style={styles.infoValue}>{agencyAddress}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Téléphone :</Text>
              <Text style={styles.infoValue}>{agencyPhone}</Text>
            </View>
          </View>
        </View>

        {/* Informations du bien */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Bien à visiter</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Type :</Text>
              <Text style={styles.infoValue}>
                {getPropertyType()}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Adresse :</Text>
              <Text style={styles.infoValue}>{property.address || 'Non spécifiée'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Surface :</Text>
              <Text style={styles.infoValue}>{Math.floor(property.features?.area || property.area || 0)} m²</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Pièces :</Text>
              <Text style={styles.infoValue}>{property.features?.rooms || property.rooms || 0}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Prix :</Text>
              <Text style={styles.infoValue}>{formatPrice(property.price || 0)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Référence :</Text>
              <Text style={styles.infoValue}>REF-{property.id}</Text>
            </View>
          </View>
        </View>

        {/* Section visite */}
        <View style={styles.visitSection}>
          <Text style={styles.visitTitle}>INFORMATIONS DE VISITE</Text>
          <View style={styles.visitGrid}>
            <View style={styles.visitColumn}>
              <View style={styles.visitField}>
                <Text style={styles.visitLabel}>Date de visite :</Text>
                <View style={styles.visitInput}></View>
              </View>
              <View style={styles.visitField}>
                <Text style={styles.visitLabel}>Heure :</Text>
                <View style={styles.visitInput}></View>
              </View>
              <View style={styles.visitField}>
                <Text style={styles.visitLabel}>Nom du visiteur :</Text>
                {visitData?.firstName || visitData?.lastName ? (
                  <Text style={styles.visitValue}>{`${visitData.firstName || ''} ${visitData.lastName || ''}`.trim()}</Text>
                ) : (
                  <View style={styles.visitInput}></View>
                )}
              </View>
            </View>
            <View style={styles.visitColumn}>
              <View style={styles.visitField}>
                <Text style={styles.visitLabel}>Téléphone :</Text>
                {visitData?.phone ? (
                  <Text style={styles.visitValue}>{visitData.phone}</Text>
                ) : (
                  <View style={styles.visitInput}></View>
                )}
              </View>
              <View style={styles.visitField}>
                <Text style={styles.visitLabel}>Pièce d'identité :</Text>
                {visitData?.idNumber ? (
                  <Text style={styles.visitValue}>{`${getIdTypeLabel(visitData.idType)} - ${visitData.idNumber}`}</Text>
                ) : (
                  <View style={styles.visitInput}></View>
                )}
              </View>
              <View style={styles.visitField}>
                <Text style={styles.visitLabel}>Agent accompagnateur :</Text>
                <View style={styles.visitInput}></View>
              </View>
            </View>
          </View>
        </View>

        {/* Mentions légales */}
        <View style={styles.legalSection}>
          <Text style={styles.legalTitle}>MENTIONS LÉGALES OBLIGATOIRES</Text>
          <Text style={styles.legalText}>
            Conformément à la loi n°08-15 du 20 juillet 2008 fixant les règles de mise en conformité des constructions et leur achèvement, 
            et au décret exécutif n°09-19 du 18 janvier 2009, ce bon de visite atteste de la visite effectuée dans le cadre d'une transaction immobilière. 
            Le visiteur déclare avoir pris connaissance de l'état du bien et des informations communiquées. 
            L'agence décline toute responsabilité en cas d'accident durant la visite. 
            Le visiteur s'engage à respecter les lieux et à suivre les consignes de sécurité.
          </Text>
        </View>

        {/* Clause d'exclusivité */}
        <View style={styles.exclusivitySection}>
          <Text style={styles.exclusivityTitle}>CLAUSE D'EXCLUSIVITÉ</Text>
          <Text style={styles.exclusivityText}>
            Le visiteur s'engage formellement, ayant visité ce bien par l'intermédiaire de l'agence mandataire, 
            à ne pas contourner cette dernière en traitant directement avec le propriétaire. 
            Cette interdiction s'applique pendant une durée de douze (12) mois à compter de la date de visite, 
            conformément aux usages professionnels et à la déontologie immobilière.
          </Text>
        </View>

        {/* Signatures */}
        <View style={styles.signatures}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureTitle}>Le Visiteur</Text>
            <Text style={styles.signatureSubtitle}>Signature et date</Text>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.signatureTitle}>L'Agent</Text>
            <Text style={styles.signatureSubtitle}>Signature et cachet</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
};
